// Utility for money formatting used across the theme.
// Provides: formatMoney(cents, format) and ensureGlobalShopifyFormat()
// Mirrors legacy Shopify.formatMoney behavior while allowing modular use.

export function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  if (cents == null || isNaN(cents)) return '0';

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  let shopFormat = '{{ amount }}';
  if (typeof window !== 'undefined') {
    if (window.__MONEY_FORMAT__) {
      shopFormat = window.__MONEY_FORMAT__;
    } else if (window.Shopify && window.Shopify.money_format) {
      shopFormat = window.Shopify.money_format;
    } else if (
      process &&
      process.env &&
      process.env.NODE_ENV !== 'production'
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        '[money] No global money format found; falling back to {{ amount }}'
      );
    }
  }
  const formatString = format || shopFormat;

  function withDelimiters(
    number,
    precision = 2,
    thousands = ',',
    decimal = '.'
  ) {
    number = (number / 100).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      '$1' + thousands
    );
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  const match = formatString.match(placeholderRegex);
  const token = match ? match[1] : 'amount';

  let value;
  switch (token) {
    case 'amount':
      value = withDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = withDelimiters(cents, 2);
  }

  return formatString.replace(placeholderRegex, value);
}

export function ensureGlobalShopifyFormat() {
  if (typeof window === 'undefined') return;
  if (!window.Shopify) window.Shopify = {};
  if (!window.Shopify.formatMoney) {
    window.Shopify.formatMoney = (cents, format) => formatMoney(cents, format);
  }
}

// Optional Intl API variant (not wired yet)
export function formatMoneyIntl(
  cents,
  { currency = 'EUR', locale = 'en-US', minimumFractionDigits = 2 } = {}
) {
  if (cents == null || isNaN(cents)) return '';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
  }).format(cents / 100);
}
